<script  setup>
import axios from "axios";
import {reactive, ref, watch, computed} from "vue";
import {ElButton, ElCard, ElMessage, ElText} from "element-plus";
import { moneyTotal, formatMoney, roundMoney } from '../../../utils/money';
import {
    isBandedTypeAddon,
    resolveBandedAddonCode,
    bandedCapacityForCode,
    scaleTransportationPricing,
    transportUnits,
} from '../../../utils/landPricing';

const props = defineProps({
    addon: Object,
    form: Object,
    index: Number,
    addonTypes: {
        type: Array,
        default: () => [],
    },
    pricingRates: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(['removeactivity', 'removeaddon', 'updated']);
const ruleFormRef = ref();
let addon = props.addon;
const eform = props.form;
const index = props.index;
const editprice = ref(false);
const imageDialog = ref(false);
const imageIndex = ref(-1);
const imageDraft = ref('');
const MAX_ADDON_IMAGES = 2;

const csrfToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta && meta.getAttribute('content')) {
        return meta.getAttribute('content');
    }
    return (typeof window !== 'undefined' && window.csrf_token) ? window.csrf_token : '';
};

const uploadImageRequest = (options) => {
    const data = new FormData();
    data.append('file', options.file);
    return axios.post('/activityapi/uploadimage', data, {
        headers: {
            'Accept': 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
        },
    }).then((response) => response.data);
};

const isUsableImage = (url) => {
    return typeof url === 'string'
        && url.trim() !== ''
        && url.toLowerCase().indexOf('noimage') === -1;
};

const imageList = computed(() => {
    let images = [];
    if (Array.isArray(addon.images) && addon.images.length) {
        images = addon.images;
    } else if (isUsableImage(addon.image)) {
        images = [addon.image];
    }
    return images.map((url) => (typeof url === 'string' ? url.trim() : '')).filter(isUsableImage).slice(0, MAX_ADDON_IMAGES);
});

const applyImages = (urls) => {
    const next = urls.filter(isUsableImage).slice(0, MAX_ADDON_IMAGES);
    addon.images = next;
    addon.image = next[0] || '';
};

const openImageEditor = (slot) => {
    if (slot < 0 && imageList.value.length >= MAX_ADDON_IMAGES) {
        ElMessage({
            message: 'An addon can have at most 2 images.',
            type: 'warning',
        });
        return;
    }
    imageIndex.value = slot;
    imageDraft.value = slot >= 0 ? (imageList.value[slot] || '') : '';
    imageDialog.value = true;
};

const saveImageDraft = () => {
    const url = imageDraft.value.trim();
    if (!url) {
        ElMessage({
            message: 'Upload an image or paste an image URL.',
            type: 'warning',
        });
        return;
    }
    if (!/^https?:\/\//i.test(url) && url.charAt(0) !== '/') {
        ElMessage({
            message: 'Image must be a valid URL.',
            type: 'warning',
        });
        return;
    }
    const next = imageList.value.slice();
    if (imageIndex.value >= 0) {
        next[imageIndex.value] = url;
    } else if (next.length >= MAX_ADDON_IMAGES) {
        ElMessage({
            message: 'An addon can have at most 2 images.',
            type: 'warning',
        });
        return;
    } else {
        next.push(url);
    }
    applyImages(next);
    imageDialog.value = false;
};

const removeImage = (slot) => {
    const next = imageList.value.slice();
    next.splice(slot, 1);
    applyImages(next);
};

const beforeImageUpload = (file) => {
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
        ElMessage({
            message: 'Upload a JPG, PNG, or WEBP image.',
            type: 'error',
        });
        return false;
    }
    if (file.size / 1024 / 1024 >= 5) {
        ElMessage({
            message: 'Image must be smaller than 5 MB.',
            type: 'error',
        });
        return false;
    }
    return true;
};

const onImageUploaded = (response) => {
    const url = response && response.url ? response.url : '';
    if (!url) {
        ElMessage({
            message: 'Image upload failed. Try again.',
            type: 'error',
        });
        return;
    }
    imageDraft.value = url;
};

const onImageUploadError = (error) => {
    const status = error && error.response ? error.response.status : (error && error.status);
    const fieldErrors = error && error.response && error.response.data && error.response.data.errors;
    let message = 'Image upload failed. Try again.';
    if (fieldErrors && fieldErrors.file && fieldErrors.file[0]) {
        message = fieldErrors.file[0];
    } else if (status === 419) {
        message = 'The page session expired. Refresh the page and upload again.';
    } else if (status === 413) {
        message = 'Image is too large. Use a file under 5 MB.';
    }
    ElMessage({
        message: message,
        type: 'error',
    });
};

const resolveUnitCost = (pricing) => {
    const unit = pricing?.unit_cost;
    if (unit !== undefined && unit !== null && unit !== '') {
        return Number(unit);
    }
    // Legacy / API rows sometimes only have total = price of one unit
    return Number(pricing?.total ?? 0);
};

const editform = reactive({
    name: addon.name,
    description: addon.description || '',
    unit_cost: resolveUnitCost(addon.pricing),
    pricing: {
        total: addon.pricing.total,
        adult: addon.pricing.adult,
        child: addon.pricing.child,
        infant: addon.pricing.infant,
        unit_cost: resolveUnitCost(addon.pricing),
        units: addon.pricing?.units ?? 0,
    },
});

const isBanded = computed(() =>
    isBandedTypeAddon({ name: editform.name, category: addon.category }, props.addonTypes)
);

const bandedCode = computed(() =>
    resolveBandedAddonCode({ name: editform.name, category: addon.category }, props.addonTypes)
);

const capacity = computed(() =>
    bandedCapacityForCode(bandedCode.value, props.pricingRates)
);

const unitLabel = computed(() =>
    bandedCode.value === 'transportation' ? 'vehicle' : 'unit'
);

const previewUnits = computed(() =>
    isBanded.value ? transportUnits(eform.adults, capacity.value) : 0
);

const removeaddon = () => {
    eform.addons.splice(index, 1);
    emit('removeaddon');
};

const refreshprice = () => {
    editform.name = addon.name;
    editform.description = addon.description || '';
    editform.unit_cost = resolveUnitCost(addon.pricing);
    editform.pricing.total = addon.pricing.total;
    editform.pricing.adult = addon.pricing.adult;
    editform.pricing.child = eform.childs > 0 ? addon.pricing.child : 0;
    editform.pricing.infant = eform.infants > 0 ? addon.pricing.infant : 0;
    editform.pricing.unit_cost = editform.unit_cost;
    editform.pricing.units = addon.pricing?.units ?? 0;
    updateupricecount();
};

watch(
    () => [editform.pricing, editform.name, editform.unit_cost],
    () => {
        updateupricecount();
    },
    { deep: true }
);

const updateupricecount = () => {
    if (isBanded.value) {
        const scaled = scaleTransportationPricing({
            unitCost: editform.unit_cost,
            adults: eform.adults,
            capacity: capacity.value,
        });
        editform.pricing.unit_cost = scaled.unit_cost;
        editform.pricing.units = scaled.units;
        editform.pricing.total = scaled.total;
        editform.pricing.adult = scaled.adult;
        editform.pricing.child = 0;
        editform.pricing.infant = 0;
        return;
    }
    editform.pricing.total = moneyTotal(
        eform.adults,
        editform.pricing.adult,
        eform.childs,
        editform.pricing.child,
        eform.infants,
        editform.pricing.infant
    );
};

const savePrice = () => {
    updateupricecount();

    if (isBanded.value) {
        addon.pricing = {
            total: roundMoney(editform.pricing.total),
            adult: roundMoney(editform.pricing.adult),
            child: 0,
            infant: 0,
            unit_cost: roundMoney(editform.unit_cost),
            units: editform.pricing.units,
        };
        if (bandedCode.value) {
            addon.category = bandedCode.value;
        }
    } else {
        const tprice = {
            total: 0,
            adult: roundMoney(editform.pricing.adult),
            child: eform.childs > 0 ? roundMoney(editform.pricing.child) : 0,
            infant: eform.infants > 0 ? roundMoney(editform.pricing.infant) : 0,
        };
        tprice.total = moneyTotal(
            eform.adults,
            tprice.adult,
            eform.childs,
            tprice.child,
            eform.infants,
            tprice.infant
        );
        addon.pricing = tprice;
    }
    addon.name = editform.name;
    const description = String(editform.description || '').trim();
    addon.description = description === '' ? null : description;
    editprice.value = false;
    emit('updated');
};

const submitForm = async (formEl) => {
    if (!formEl) return;
    await formEl.validate((valid) => {
        if (valid) {
            savePrice();
        }
    });
};
</script>

<template>
    <el-card class="mb-3" shadow="never" :body-style="{ padding: 0 }">
        <div class="flex">
            <div class="p-2" style="width: 100%;">
                <div>
                    <el-text tag="b" size="large">{{ addon.name }}</el-text>
                    <p v-if="addon.description" class="addon-description">
                        <el-text size="small" type="info">{{ addon.description }}</el-text>
                    </p>
                    <el-text
                        v-if="addon.pricing?.units"
                        class="ml-2"
                        size="small"
                        type="info"
                    >
                        {{ addon.pricing.units }} {{ unitLabel }}(s) @ ₹{{ formatMoney(addon.pricing.unit_cost) }}
                    </el-text>
                </div>
            </div>
            <div class="p-2">
                <div class="flex">
                    <el-dropdown trigger="hover">
                        <div>
                            <el-text tag="b" size="large">₹{{ formatMoney(addon.pricing.total) }} </el-text>
                            <el-text
                                class="ml-2"
                                tag="b"
                                size="small"
                                type="info"
                                @click="refreshprice(); editprice = true"
                            >Edit</el-text>
                        </div>
                        <template #dropdown>
                            <p v-if="addon.pricing?.unit_cost != null">
                                Unit - ₹ {{ formatMoney(addon.pricing.unit_cost) }}
                            </p>
                            <p>
                                Adult - ₹ {{ formatMoney(addon.pricing.adult) }}
                            </p>
                            <p v-if="eform.childs > 0">
                                Child - ₹ {{ formatMoney(addon.pricing.child) }}
                            </p>
                            <p v-if="eform.infants > 0">
                                Infant - ₹ {{ formatMoney(addon.pricing.infant) }}
                            </p>
                        </template>
                    </el-dropdown>

                    <el-button class="ml-2" type="danger" size="small" @click="removeaddon">Remove</el-button>
                </div>
            </div>
        </div>

        <div class="addon-images">
            <div class="addon-images-head">
                <el-text tag="b" size="small">Images ({{ imageList.length }}/2)</el-text>
            </div>
            <div class="addon-images-row">
                <div v-for="(url, imageIdx) in imageList" :key="imageIdx" class="addon-image-card">
                    <img :src="url" alt="" />
                    <div class="addon-image-actions">
                        <el-button size="small" @click="openImageEditor(imageIdx)">Edit</el-button>
                        <el-button size="small" type="danger" @click="removeImage(imageIdx)">Remove</el-button>
                    </div>
                </div>
                <el-button
                    v-if="imageList.length < 2"
                    size="small"
                    type="primary"
                    @click="openImageEditor(-1)"
                >Add Image</el-button>
            </div>
        </div>
    </el-card>

    <el-dialog v-model="editprice" title="Edit Addon" width="50%">
        <el-form
            ref="ruleFormRef"
            :model="editform"
            status-icon
            label-width="auto"
            class="demo-ruleForm"
            label-position="top"
            size="small"
        >
            <el-row :gutter="20">
                <el-col :span="24">
                    <el-form-item
                        label="Name"
                        prop="name"
                        :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                    >
                        <el-input v-model="editform.name" placeholder="Name" />
                    </el-form-item>
                </el-col>
            </el-row>
            <el-row :gutter="20">
                <el-col :span="24">
                    <el-form-item label="Description">
                        <el-input
                            v-model="editform.description"
                            type="textarea"
                            :rows="3"
                            placeholder="Optional"
                        />
                    </el-form-item>
                </el-col>
            </el-row>

            <template v-if="isBanded">
                <el-alert
                    class="mb-3"
                    :title="`${previewUnits} ${unitLabel}(s) for ${eform.adults} adult(s) (capacity ${capacity})`"
                    type="info"
                    :closable="false"
                    show-icon
                />
                <el-row :gutter="20">
                    <el-col :span="8">
                        <el-form-item
                            :label="'Unit cost (per ' + unitLabel + ')'"
                            prop="unit_cost"
                            :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                        >
                            <el-input v-model="editform.unit_cost" placeholder="Unit cost" type="number" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item :label="unitLabel === 'vehicle' ? 'Vehicles' : 'Units'">
                            <el-input :model-value="previewUnits" readonly />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="Total Pricing">
                            <el-input v-model="editform.pricing.total" type="number" readonly />
                        </el-form-item>
                    </el-col>
                </el-row>
            </template>

            <template v-else>
                <el-row :gutter="20">
                    <el-col :span="4">
                        <el-form-item label="Adults">
                            <el-input v-model="eform.adults" placeholder="Adults" readonly />
                        </el-form-item>
                    </el-col>
                    <el-col :span="4">
                        <el-form-item
                            label="Adult Pricing"
                            prop="pricing.adult"
                            :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                        >
                            <el-input v-model="editform.pricing.adult" placeholder="Adult Pricing" type="number" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20" v-if="eform.childs > 0">
                    <el-col :span="4">
                        <el-form-item label="Child">
                            <el-input v-model="eform.childs" placeholder="Child" readonly />
                        </el-form-item>
                    </el-col>
                    <el-col :span="4">
                        <el-form-item
                            label="Child Pricing"
                            prop="pricing.child"
                            :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                        >
                            <el-input v-model="editform.pricing.child" placeholder="Child Pricing" type="number" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20" v-if="eform.infants > 0">
                    <el-col :span="4">
                        <el-form-item label="Infants">
                            <el-input v-model="eform.infants" placeholder="Infants" readonly />
                        </el-form-item>
                    </el-col>
                    <el-col :span="4">
                        <el-form-item
                            label="Infants Pricing"
                            prop="pricing.infant"
                            :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                        >
                            <el-input v-model="editform.pricing.infant" placeholder="Infants Pricing" type="number" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20">
                    <el-col :span="4" />
                    <el-col :span="4">
                        <el-form-item
                            label="Total Pricing"
                            prop="pricing.total"
                            :rules="{ required: true, message: 'Required', trigger: ['blur', 'change'] }"
                        >
                            <el-input v-model="editform.pricing.total" placeholder="Total Pricing" type="number" readonly />
                        </el-form-item>
                    </el-col>
                </el-row>
            </template>
        </el-form>

        <template #footer>
            <div class="dialog-footer">
                <el-button @click="editprice = false">Cancel</el-button>
                <el-button type="primary" @click="submitForm(ruleFormRef)">Save</el-button>
            </div>
        </template>
    </el-dialog>

    <el-dialog
        v-model="imageDialog"
        :title="imageIndex >= 0 ? 'Edit Image' : 'Add Image'"
        width="50%"
    >
        <div v-if="imageDraft" class="addon-image-preview">
            <img :src="imageDraft" alt="" />
        </div>
        <el-upload
            class="mb-3"
            action="/activityapi/uploadimage"
            :http-request="uploadImageRequest"
            :show-file-list="false"
            accept="image/jpeg,image/png,image/webp"
            :before-upload="beforeImageUpload"
            :on-success="onImageUploaded"
            :on-error="onImageUploadError"
        >
            <el-button type="primary" size="small">Upload image</el-button>
            <template #tip>
                <div class="el-upload__tip">JPG, PNG, or WEBP. Maximum 5 MB. An addon can have 0 to 2 images.</div>
            </template>
        </el-upload>
        <el-form label-position="top" size="small">
            <el-form-item label="Image URL">
                <el-input v-model="imageDraft" placeholder="https://..." />
            </el-form-item>
        </el-form>
        <template #footer>
            <div class="dialog-footer">
                <el-button @click="imageDialog = false">Cancel</el-button>
                <el-button type="primary" @click="saveImageDraft">Save</el-button>
            </div>
        </template>
    </el-dialog>
</template>

<style scoped>
.addon-description {
    margin: 4px 0 0;
}
.addon-images {
    padding: 0 8px 12px;
}
.addon-images-head {
    margin-bottom: 8px;
}
.addon-images-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
}
.addon-image-card img,
.addon-image-preview img {
    width: 160px;
    height: 96px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #ebeef5;
    display: block;
    background: #f5f7fa;
}
.addon-image-actions {
    display: flex;
    gap: 6px;
    margin-top: 6px;
}
.addon-image-preview {
    margin-bottom: 12px;
}
</style>
